import { ContentType } from "@brookbush/database";
import { type ContextualCTA, UserAction, UserType } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import { useRouter } from "next/router";
import posthog from "posthog-js";
import { useMemo } from "react";
import useSWR from "swr";

import useSale from "./useSale";
import { CONVERSION_PAGE_TO_CONTENT_MAP } from "~/utils/constants";

const PATH_TO_CONTENT_TYPE_MAP: Record<string, ContentType> = {
	"/articles/:slug": ContentType.ARTICLE,
	"/videos/:slug": ContentType.VIDEO,
	"/courses/:slug": ContentType.COURSE,
	"/glossary/:slug": ContentType.GLOSSARY,
};

const useCta = () => {
	const { userId } = useAuth();
	const { asPath } = useRouter();
	const { sale } = useSale();

	const path = asPath.split("?")[0].split("#")[0];

	const slug = useRouter().query.slug as string;

	const contentType = useMemo(
		() =>
			Object.entries(PATH_TO_CONTENT_TYPE_MAP).find(([key]) =>
				new URLPattern({
					pathname: key,
				}).test({
					pathname: path,
				}),
			)?.[1],
		[path],
	);

	const defaultCta = useMemo(
		() =>
			(Object.entries(CONVERSION_PAGE_TO_CONTENT_MAP).find(([key]) =>
				new URLPattern({
					pathname: key,
				}).test({
					pathname: path,
				}),
			)?.[1] || CONVERSION_PAGE_TO_CONTENT_MAP["default"])[
				userId ? UserType.User : UserType.Guest
			],
		[path, userId],
	)!;

	const { data: contextualCta, isLoading } = useSWR<ContextualCTA>(
		slug && contentType && `/users/cta/${contentType}/${slug}`,
	);

	let isEnabled: boolean | undefined;
	if (contextualCta) {
		isEnabled =
			process.env.NODE_ENV === "development" ||
			posthog.isFeatureEnabled(
				userId ? "contextual-membership" : "contextual-sign-up",
			);
	}

	const cta: ContextualCTA = sale?.promoCode
		? {
				userAction: UserAction.BECOME_A_MEMBER,
				...sale,
			}
		: (isEnabled ? contextualCta : undefined) || defaultCta;

	return {
		cta,
		isLoadingCta: isLoading,
		isCtaEnabled: isEnabled,
		hasCta: !!contextualCta,
	};
};

export default useCta;
